import type { ContactDetail } from '@rh/shared';
import { classer, resoudreParRelecture } from './classement';
import type { Diffusion } from './diffusion';
import type { GardeRefus, GardeRefusFin, StockageFile } from './stockage';
import type { TransportContacts } from './transport';
import type { CleSaisie, DemandeMiseEnFile, EnvoiContact, RefusEnvoi, ResultatMiseEnFile } from './types';
import { OCCUPE, type Verrou } from './verrou';

/**
 * Moteur de la file d'envoi : une seule voie d'écriture pour le panneau de
 * contact, en ligne comme hors ligne. Une tournée prend le verrou entre
 * onglets, purge les saisies expirées puis envoie, en série et dans l'ordre de
 * saisie, les éléments du compte connecté : d'abord le PUT, puis la clôture ou
 * l'évaluation. Toutes les dépendances sont injectées (tests sans navigateur).
 */

export const VERROU_ENVOI = 'rh-app:file:envoi';
/** Attente avant un nouvel essai, selon le nombre de tentatives (± 20 %). */
export const ATTENTES_MS: readonly number[] = [5_000, 15_000, 30_000, 60_000, 120_000, 300_000];
export const PASSAGES_ENREGISTREMENT_MAX = 3;

const MINUTERIE_MIN_MS = 5_000;
const MINUTERIE_MAX_MS = 300_000;
const ARRET_MAX_MS = 20_000;

export type RaisonDeclenchement =
  | 'demarrage'
  | 'connexion'
  | 'session-reprise'
  | 'mise-en-file'
  | 'autre-onglet'
  | 'en-ligne'
  | 'visible'
  | 'minuterie'
  | 'manuel';

export type ArretTournee =
  | 'fini'
  | 'panne'
  | 'session'
  | 'autre_compte'
  | 'occupe'
  | 'hors_ligne'
  | 'sans_utilisateur'
  | 'arrete';

export interface BilanTournee {
  arret: ArretTournee;
  envoyes: string[];
  refuses: string[];
  prochainEssai: number | null;
}

export type EvenementMoteur =
  | { type: 'tournee-debut'; total: number }
  | { type: 'envoi-debut'; contactId: string; rang: number; total: number }
  | { type: 'envoye'; contactId: string; contact: ContactDetail | null }
  | { type: 'refuse'; contactId: string; refus: RefusEnvoi }
  | { type: 'panne'; contactId: string; message: string; prochaineTentativeLe: number }
  | { type: 'tournee-fin'; bilan: BilanTournee }
  | { type: 'file-modifiee' };

export interface DependancesMoteur {
  stockage: StockageFile;
  transport: TransportContacts;
  verrou: Verrou;
  diffusion: Diffusion;
  /** maintenantServeur */
  horloge: () => number;
  /** api.utilisateurDuJeton, null hors état connecté */
  utilisateurCourant: () => string | null;
  /** false seulement si navigator.onLine === false */
  reseauProbable: () => boolean;
  /** défaut setTimeout */
  planifier?: (f: () => void, ms: number) => () => void;
  aleatoire?: () => number;
}

/** Issue du traitement d'un contact : les trois premières arrêtent la tournée. */
type IssueContact = 'panne_reseau' | 'session' | 'autre_compte' | 'envoye' | 'refuse' | 'panne_element' | 'ignore';

const bilanVide = (arret: ArretTournee, prochainEssai: number | null = null): BilanTournee => ({
  arret,
  envoyes: [],
  refuses: [],
  prochainEssai,
});

function planifierParDefaut(f: () => void, ms: number): () => void {
  const minuteur = setTimeout(f, ms);
  return () => clearTimeout(minuteur);
}

export class MoteurEnvois {
  private readonly stockage: StockageFile;
  private readonly transport: TransportContacts;
  private readonly planifier: (f: () => void, ms: number) => () => void;
  private readonly aleatoire: () => number;

  private chaine: Promise<BilanTournee> | null = null;
  private relancer = false;
  private arrete = false;
  private contactEnCours: string | null = null;
  private traitementEnCours: Promise<IssueContact> | null = null;
  private annulerMinuterie: (() => void) | null = null;
  private readonly ecouteurs = new Set<(evenement: EvenementMoteur) => void>();
  /** Dernière issue connue par contact depuis sa dernière mise en file (pour `attendreContact`). */
  private readonly issues = new Map<string, 'envoye' | 'refuse'>();
  private readonly finsDeChaine = new Set<() => void>();

  constructor(private readonly d: DependancesMoteur) {
    this.stockage = d.stockage;
    this.transport = d.transport;
    this.planifier = d.planifier ?? planifierParDefaut;
    this.aleatoire = d.aleatoire ?? Math.random;
  }

  // -------------------------------------------------------------------------
  // Interface
  // -------------------------------------------------------------------------

  async mettreEnFile(demande: DemandeMiseEnFile): Promise<ResultatMiseEnFile> {
    const resultat = await this.stockage.mettreEnFile(demande, this.d.horloge());
    if (resultat.ok) {
      this.issues.delete(demande.contact.id);
      this.signalerFileModifiee();
      this.declencher('mise-en-file');
    }
    return resultat;
  }

  /** Lance une tournée, ou en redemande une à la fin de celle en cours. */
  declencher(_raison: RaisonDeclenchement): void {
    if (this.arrete) return;
    void this.tournee();
  }

  tournee(): Promise<BilanTournee> {
    if (this.arrete) return Promise.resolve(bilanVide('arrete'));
    if (this.chaine) {
      this.relancer = true;
      return this.chaine;
    }
    const chaine = this.enchainer().finally(() => {
      if (this.chaine === chaine) this.chaine = null;
      // Un déclenchement arrivé entre la dernière tournée et ici n'est pas perdu.
      if (this.relancer && !this.arrete) {
        void this.tournee();
        return;
      }
      for (const fin of [...this.finsDeChaine]) fin();
    });
    this.chaine = chaine;
    return chaine;
  }

  /** « Envoyer maintenant » : les attentes entre essais sont levées. */
  async reessayerMaintenant(): Promise<BilanTournee> {
    const utilisateurId = this.d.utilisateurCourant();
    if (utilisateurId) await this.stockage.reprendreMaintenant(utilisateurId);
    return this.tournee();
  }

  /**
   * Issue d'un contact qui vient d'être mis en file, attendue au plus
   * `delaiMs` : les boutons n'attendent jamais le réseau plus longtemps. Rend
   * « en attente » dès qu'il est clair que rien ne partira tout de suite.
   */
  attendreContact(contactId: string, delaiMs: number): Promise<'envoye' | 'refuse' | 'en_attente'> {
    const connue = this.issues.get(contactId);
    if (connue) return Promise.resolve(connue);
    if (this.arrete || !this.chaine) return Promise.resolve('en_attente');
    return new Promise((resoudre) => {
      let fini = false;
      let desabonner: () => void = () => undefined;
      let annuler: () => void = () => undefined;
      const terminer = (issue: 'envoye' | 'refuse' | 'en_attente') => {
        if (fini) return;
        fini = true;
        desabonner();
        annuler();
        this.finsDeChaine.delete(finDeChaine);
        resoudre(issue);
      };
      const finDeChaine = () => terminer(this.issues.get(contactId) ?? 'en_attente');
      this.finsDeChaine.add(finDeChaine);
      desabonner = this.abonner((evenement) => {
        if (evenement.type === 'envoye' && evenement.contactId === contactId) terminer('envoye');
        else if (evenement.type === 'refuse' && evenement.contactId === contactId) terminer('refuse');
        else if (evenement.type === 'panne' && evenement.contactId === contactId) terminer('en_attente');
      });
      annuler = this.planifier(() => terminer('en_attente'), delaiMs);
    });
  }

  /** Après un PUT refusé sur un contact clos sans évaluation : l'évaluation part seule. */
  async envoyerEvaluationSeule(cle: CleSaisie): Promise<void> {
    await this.attendreTraitement(cle[1]);
    await this.stockage.retirerEnregistrement(cle);
    this.issues.delete(cle[1]);
    this.signalerFileModifiee();
    this.declencher('manuel');
  }

  /** Efface une saisie ; si elle est en cours d'envoi, après la fin de cet envoi. */
  async abandonner(cle: CleSaisie): Promise<void> {
    await this.attendreTraitement(cle[1]);
    await this.stockage.abandonner(cle);
    this.issues.delete(cle[1]);
    this.signalerFileModifiee();
  }

  enCours(): { contactId: string | null } | null {
    return this.chaine ? { contactId: this.contactEnCours } : null;
  }

  abonner(ecouteur: (evenement: EvenementMoteur) => void): () => void {
    this.ecouteurs.add(ecouteur);
    return () => {
      this.ecouteurs.delete(ecouteur);
    };
  }

  /**
   * Plus aucune tournée ni minuterie. La requête en vol n'est pas coupée
   * (l'API la reconnaîtra si elle revient) : on attend au plus `delaiMaxMs`
   * qu'elle se termine.
   */
  async arreter(delaiMaxMs: number = ARRET_MAX_MS): Promise<void> {
    this.arrete = true;
    this.relancer = false;
    this.annulerMinuterie?.();
    this.annulerMinuterie = null;
    const chaine = this.chaine;
    if (!chaine) return;
    await new Promise<void>((resoudre) => {
      const annuler = this.planifier(resoudre, delaiMaxMs);
      const fin = () => {
        annuler();
        resoudre();
      };
      chaine.then(fin, fin);
    });
  }

  reprendre(): void {
    this.arrete = false;
  }

  // -------------------------------------------------------------------------
  // Tournées
  // -------------------------------------------------------------------------

  private async enchainer(): Promise<BilanTournee> {
    let bilan: BilanTournee;
    do {
      this.relancer = false;
      bilan = await this.uneTournee();
      this.emettre({ type: 'tournee-fin', bilan });
    } while (this.relancer && !this.arrete);
    this.planifierSuivante(bilan);
    return bilan;
  }

  private async uneTournee(): Promise<BilanTournee> {
    const utilisateurId = this.d.utilisateurCourant();
    if (!utilisateurId) return bilanVide('sans_utilisateur');
    try {
      // navigator.onLine n'est qu'un indice : faux, on ne compte aucune tentative ; vrai, on essaie.
      if (!this.d.reseauProbable()) {
        return bilanVide('hors_ligne', await this.stockage.prochaineEcheance(utilisateurId));
      }
      const issue = await this.d.verrou.essayer(VERROU_ENVOI, () => this.tourneeSousVerrou(utilisateurId));
      if (issue === OCCUPE) return bilanVide('occupe', await this.stockage.prochaineEcheance(utilisateurId));
      return issue;
    } catch (erreur) {
      // Stockage en faute : rien n'est perdu, on réessaiera plus tard.
      console.error(erreur);
      return bilanVide('panne', this.d.horloge() + ATTENTES_MS[3]!);
    }
  }

  private async tourneeSousVerrou(utilisateurId: string): Promise<BilanTournee> {
    const maintenant = this.d.horloge();
    if ((await this.stockage.purgerExpires(maintenant)) > 0) this.signalerFileModifiee();
    const cles = await this.stockage.listerAEnvoyer(utilisateurId, maintenant);
    const envoyes: string[] = [];
    const refuses: string[] = [];
    let arret: ArretTournee = 'fini';
    this.emettre({ type: 'tournee-debut', total: cles.length });

    for (const [rang, cle] of cles.entries()) {
      if (this.arrete) {
        arret = 'arrete';
        break;
      }
      // Session reprise sous un autre compte entre deux envois : plus rien ne part.
      if (this.d.utilisateurCourant() !== utilisateurId) {
        arret = 'autre_compte';
        break;
      }
      this.contactEnCours = cle[1];
      this.emettre({ type: 'envoi-debut', contactId: cle[1], rang: rang + 1, total: cles.length });
      const traitement = this.traiter(cle, utilisateurId);
      this.traitementEnCours = traitement;
      let issue: IssueContact;
      try {
        issue = await traitement;
      } finally {
        this.contactEnCours = null;
        this.traitementEnCours = null;
      }
      if (issue === 'envoye') envoyes.push(cle[1]);
      else if (issue === 'refuse') refuses.push(cle[1]);
      else if (issue === 'panne_reseau') arret = 'panne';
      else if (issue === 'session' || issue === 'autre_compte') arret = issue;
      if (arret !== 'fini') break;
    }

    return { arret, envoyes, refuses, prochainEssai: await this.stockage.prochaineEcheance(utilisateurId) };
  }

  private planifierSuivante(bilan: BilanTournee): void {
    this.annulerMinuterie?.();
    this.annulerMinuterie = null;
    if (this.arrete || bilan.prochainEssai === null) return;
    // Sans session valable pour ce compte, c'est la session retrouvée qui relancera.
    if (['sans_utilisateur', 'session', 'autre_compte', 'arrete'].includes(bilan.arret)) return;
    const attente = Math.min(Math.max(bilan.prochainEssai - this.d.horloge(), MINUTERIE_MIN_MS), MINUTERIE_MAX_MS);
    this.annulerMinuterie = this.planifier(() => {
      this.annulerMinuterie = null;
      this.declencher('minuterie');
    }, attente);
  }

  // -------------------------------------------------------------------------
  // Un contact
  // -------------------------------------------------------------------------

  private async traiter(cle: CleSaisie, utilisateurId: string): Promise<IssueContact> {
    let envoi = await this.stockage.lire(cle);
    if (!envoi || envoi.etat !== 'en_attente' || envoi.utilisateurId !== utilisateurId) return 'ignore';

    // Enregistrement : une fusion arrivée pendant l'envoi repart, au plus trois fois de suite.
    for (let passage = 0; envoi.enregistrement && passage < PASSAGES_ENREGISTREMENT_MAX; passage += 1) {
      const { corps, revision } = envoi.enregistrement;
      await this.stockage.noterTentative(cle, this.d.horloge());
      let contact: ContactDetail;
      try {
        contact = await this.transport.modifier(cle[1], corps, utilisateurId);
      } catch (erreur) {
        // La fin ne part jamais sur un enregistrement non confirmé.
        return this.echec(cle, envoi, 'enregistrement', erreur, { etape: 'enregistrement', revision }, utilisateurId);
      }
      const suite = await this.stockage.confirmerEnregistrement(cle, revision);
      if (suite === 'termine') return this.envoye(cle, utilisateurId, contact);
      if (suite === 'absent') return 'ignore';
      const relu = await this.stockage.lire(cle);
      if (!relu || relu.etat !== 'en_attente') return 'ignore';
      envoi = relu;
    }
    if (envoi.enregistrement) return 'ignore';

    const fin = envoi.fin;
    if (!fin) return 'ignore';
    await this.stockage.noterTentative(cle, this.d.horloge());
    let contact: ContactDetail;
    try {
      contact =
        fin.genre === 'cloture'
          ? await this.transport.cloturer(cle[1], { envoiId: fin.envoiId, termineLe: fin.termineLe }, utilisateurId)
          : await this.transport.evaluer(
              cle[1],
              {
                ...fin.entree,
                envoiId: fin.envoiId,
                evalueeLe: fin.evalueeLe,
                baremeId: fin.bareme.id,
                baremeVersion: fin.bareme.version,
              },
              utilisateurId,
            );
    } catch (erreur) {
      return this.echec(cle, envoi, 'fin', erreur, { etape: 'fin', envoiId: fin.envoiId }, utilisateurId);
    }
    await this.stockage.terminer(cle, fin.envoiId);
    return this.envoye(cle, utilisateurId, contact);
  }

  private async echec(
    cle: CleSaisie,
    envoi: EnvoiContact,
    etape: 'enregistrement' | 'fin',
    erreur: unknown,
    garde: GardeRefus | GardeRefusFin,
    utilisateurId: string,
  ): Promise<IssueContact> {
    const classement = classer(erreur);
    switch (classement.genre) {
      case 'autre_compte':
      case 'session':
        // Rien n'est marqué : la saisie attend la session du compte qui l'a faite.
        return classement.genre;
      case 'panne':
        return this.panne(cle, classement.portee, classement.message);
      case 'refus':
        return this.refuser(
          cle,
          utilisateurId,
          { ...classement.refus, etape, le: this.d.horloge(), action: null },
          garde,
        );
      case 'a_verifier':
        return this.verifier(cle, envoi, etape, classement, garde, utilisateurId);
    }
  }

  private async panne(cle: CleSaisie, portee: 'reseau' | 'element', message: string): Promise<IssueContact> {
    const tentatives = (await this.stockage.lire(cle))?.tentatives ?? 1;
    const base = ATTENTES_MS[Math.min(Math.max(tentatives, 1), ATTENTES_MS.length) - 1]!;
    const maintenant = this.d.horloge();
    const prochaineTentativeLe = maintenant + Math.round(base * (0.8 + 0.4 * this.aleatoire()));
    await this.stockage.planifierReprise(cle, prochaineTentativeLe, { le: maintenant, message });
    this.emettre({ type: 'panne', contactId: cle[1], message, prochaineTentativeLe });
    return portee === 'reseau' ? 'panne_reseau' : 'panne_element';
  }

  private async refuser(
    cle: CleSaisie,
    utilisateurId: string,
    refus: RefusEnvoi,
    garde: GardeRefus | GardeRefusFin,
  ): Promise<IssueContact> {
    // Refus d'une révision déjà remplacée : ignoré, la nouvelle part au tour suivant.
    if (!(await this.stockage.marquerRefus(cle, refus, garde))) return 'ignore';
    this.issues.set(cle[1], 'refuse');
    this.emettre({ type: 'refuse', contactId: cle[1], refus });
    this.d.diffusion.publier({ type: 'envoi-termine', utilisateurId, contactId: cle[1], issue: 'refuse' });
    return 'refuse';
  }

  /** 409 : notre fin est peut-être déjà passée (réponse perdue, autre onglet). On relit le contact. */
  private async verifier(
    cle: CleSaisie,
    envoi: EnvoiContact,
    etape: 'enregistrement' | 'fin',
    origine: { code: string | null; message: string },
    garde: GardeRefus | GardeRefusFin,
    utilisateurId: string,
  ): Promise<IssueContact> {
    const refus409 = (code: string | null, message: string, action: RefusEnvoi['action']): RefusEnvoi => ({
      etape,
      statut: 409,
      code,
      message,
      details: null,
      bareme: null,
      le: this.d.horloge(),
      contenuEfface: false,
      action,
    });

    let lu: ContactDetail;
    try {
      lu = await this.transport.lire(cle[1], utilisateurId);
    } catch (erreur) {
      const relecture = classer(erreur);
      switch (relecture.genre) {
        case 'autre_compte':
        case 'session':
          return relecture.genre;
        case 'panne':
          return this.panne(cle, relecture.portee, relecture.message);
        case 'a_verifier':
          return this.panne(cle, 'element', relecture.message);
        case 'refus':
          if (relecture.refus.contenuEfface) {
            return this.refuser(cle, utilisateurId, { ...relecture.refus, etape, le: this.d.horloge(), action: null }, garde);
          }
          // Relecture refusée sans rien apprendre : le 409 d'origine tient.
          return this.refuser(cle, utilisateurId, refus409(origine.code, origine.message, null), garde);
      }
    }

    const resolution = resoudreParRelecture(envoi, etape, lu, origine);
    if (resolution.genre === 'deja_fait') {
      if (envoi.fin) await this.stockage.terminer(cle, envoi.fin.envoiId);
      return this.envoye(cle, utilisateurId, lu);
    }
    return this.refuser(cle, utilisateurId, refus409(resolution.code, resolution.message, resolution.action), garde);
  }

  // -------------------------------------------------------------------------
  // Événements
  // -------------------------------------------------------------------------

  private envoye(cle: CleSaisie, utilisateurId: string, contact: ContactDetail | null): IssueContact {
    this.issues.set(cle[1], 'envoye');
    this.emettre({ type: 'envoye', contactId: cle[1], contact });
    this.d.diffusion.publier({ type: 'envoi-termine', utilisateurId, contactId: cle[1], issue: 'envoye' });
    return 'envoye';
  }

  private signalerFileModifiee(): void {
    this.emettre({ type: 'file-modifiee' });
    this.d.diffusion.publier({ type: 'file-modifiee' });
  }

  private async attendreTraitement(contactId: string): Promise<void> {
    while (this.contactEnCours === contactId && this.traitementEnCours) {
      await this.traitementEnCours.catch(() => undefined);
    }
  }

  /** Un écouteur en faute ne doit ni priver les suivants ni interrompre la file. */
  private emettre(evenement: EvenementMoteur): void {
    for (const ecouteur of [...this.ecouteurs]) {
      try {
        ecouteur(evenement);
      } catch (erreur) {
        console.error(erreur);
      }
    }
  }
}
